import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  CurrencyRupeeIcon,
  CalendarIcon,
} from "@heroicons/react/outline";
import {
  getCollectionStatusThunk,
  recordCollectionPaymentThunk,
  waiveCollectionThunk,
  resolveCollectionThunk,
} from "../../../store/thunks/jewelleryPlan/cancellationThunks";
import {
  selectCurrentCollection,
  selectCollectionsLoading,
  selectCollectionError,
  selectCollectionActionLoading,
} from "../../../store/slices/jewelleryPlan/cancellationSlice";

const CollectionDetails = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const collection = useSelector(selectCurrentCollection);
  const loading = useSelector(selectCollectionsLoading);
  const error = useSelector(selectCollectionError);
  const actionLoading = useSelector(selectCollectionActionLoading);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showWaiveModal, setShowWaiveModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);

  const [paymentData, setPaymentData] = useState({
    amount: "",
    method: "CASH",
    reference: "",
  });

  const [waiveData, setWaiveData] = useState({
    reason: "",
    approvalNote: "",
  });

  const [resolveNote, setResolveNote] = useState("");

  useEffect(() => {
    dispatch(getCollectionStatusThunk(collectionId));
  }, [dispatch, collectionId]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        bg: "bg-warning-light",
        text: "text-warning-dark",
        icon: ClockIcon,
        label: "Pending",
      },
      NOTIFIED: {
        bg: "bg-secondary-light",
        text: "text-secondary-dark",
        icon: ExclamationCircleIcon,
        label: "Notified",
      },
      PARTIAL_COLLECTED: {
        bg: "bg-primary-light",
        text: "text-primary-dark",
        icon: ClockIcon,
        label: "Partial Collected",
      },
      COLLECTED: {
        bg: "bg-success-light",
        text: "text-success-dark",
        icon: CheckCircleIcon,
        label: "Collected",
      },
      WAIVED: {
        bg: "bg-neutral-light",
        text: "text-neutral-dark",
        icon: CheckCircleIcon,
        label: "Waived",
      },
      WRITTEN_OFF: {
        bg: "bg-danger-light",
        text: "text-danger-dark",
        icon: XCircleIcon,
        label: "Written Off",
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}
      >
        <Icon className="h-5 w-5 mr-2" />
        {config.label}
      </span>
    );
  };

  const handleRecordPayment = async () => {
    if (!paymentData.amount || !paymentData.method) {
      alert("Please fill all required fields");
      return;
    }

    try {
      await dispatch(
        recordCollectionPaymentThunk({
          collectionRequestId: collectionId,
          paymentData: {
            amount: parseFloat(paymentData.amount),
            method: paymentData.method,
            reference: paymentData.reference,
          },
        })
      ).unwrap();

      setShowPaymentModal(false);
      setPaymentData({ amount: "", method: "CASH", reference: "" });
      dispatch(getCollectionStatusThunk(collectionId));
    } catch (error) {
      alert("Failed to record payment: " + error);
    }
  };

  const handleWaive = async () => {
    if (!waiveData.reason) {
      alert("Please provide a reason for waiving");
      return;
    }

    try {
      await dispatch(
        waiveCollectionThunk({
          collectionRequestId: collectionId,
          waiveData: {
            reason: waiveData.reason,
            approvalNote: waiveData.approvalNote,
          },
        })
      ).unwrap();

      setShowWaiveModal(false);
      setWaiveData({ reason: "", approvalNote: "" });
      dispatch(getCollectionStatusThunk(collectionId));
    } catch (error) {
      alert("Failed to waive collection: " + error);
    }
  };

  const handleResolve = async () => {
    try {
      await dispatch(
        resolveCollectionThunk({
          collectionRequestId: collectionId,
          resolutionData: { note: resolveNote },
        })
      ).unwrap();

      setShowResolveModal(false);
      setResolveNote("");
      dispatch(getCollectionStatusThunk(collectionId));
    } catch (error) {
      alert("Failed to resolve collection: " + error);
    }
  };

  const paymentMethods = [
    { value: "CASH", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "CARD", label: "Card" },
    { value: "BANK_TRANSFER", label: "Bank Transfer" },
    { value: "CHEQUE", label: "Cheque" },
  ];

  if (loading && !collection) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-danger-light border border-danger rounded-lg p-6">
          <div className="flex items-start">
            <XCircleIcon className="h-6 w-6 text-danger mt-0.5 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-danger-dark">Error</h3>
              <p className="mt-2 text-sm text-danger-dark">{error}</p>
              <button
                onClick={() => navigate("/jewellery/collections")}
                className="mt-4 px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark"
              >
                Back to Collections
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <DocumentTextIcon className="h-12 w-12 text-neutral mx-auto mb-4" />
          <p className="text-neutral-dark">Collection request not found</p>
          <button
            onClick={() => navigate("/jewellery/collections")}
            className="mt-4 text-primary hover:text-primary-dark"
          >
            Back to Collections
          </button>
        </div>
      </div>
    );
  }

  const remaining = collection.amount - (collection.collectedAmount || 0);
  const percentage =
    ((collection.collectedAmount || 0) / collection.amount) * 100;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/jewellery/collections")}
          className="flex items-center text-primary hover:text-primary-dark mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Collections
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Collection Details</h1>
            <p className="mt-1 text-sm text-neutral-dark">
              Plan #{collection.planId?.planNumber || "N/A"}
            </p>
          </div>
          {getStatusBadge(collection.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Amount Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-danger-light border border-danger rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-danger-dark">Total Due</p>
                  <p className="text-2xl font-bold text-danger-dark mt-1">
                    {formatAmount(collection.amount)}
                  </p>
                </div>
                <CurrencyRupeeIcon className="h-10 w-10 text-danger" />
              </div>
            </div>

            <div className="bg-success-light border border-success rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-success-dark">Collected</p>
                  <p className="text-2xl font-bold text-success-dark mt-1">
                    {formatAmount(collection.collectedAmount || 0)}
                  </p>
                </div>
                <CheckCircleIcon className="h-10 w-10 text-success" />
              </div>
            </div>

            <div className="bg-warning-light border border-warning rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warning-dark">Remaining</p>
                  <p className="text-2xl font-bold text-warning-dark mt-1">
                    {formatAmount(remaining)}
                  </p>
                </div>
                <ClockIcon className="h-10 w-10 text-warning" />
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {collection.collectedAmount > 0 && (
            <div className="bg-background rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm text-neutral-dark">Collection Progress</p>
                <p className="text-sm font-medium text-text">
                  {percentage.toFixed(1)}%
                </p>
              </div>
              <div className="w-full bg-neutral-light rounded-full h-4">
                <div
                  className="bg-success h-4 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Basic Details */}
          <div className="bg-background rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-text mb-4">
              Collection Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-neutral-dark">Plan Number</p>
                <p className="font-medium">
                  {collection.planId?.planNumber || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Customer ID</p>
                <p className="font-medium">{collection.customerId || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Branch</p>
                <p className="font-medium">{collection.branchCode || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Created Date</p>
                <p className="font-medium">
                  {formatDate(collection.createdAt)}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Last Updated</p>
                <p className="font-medium">
                  {formatDate(collection.updatedAt)}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Status</p>
                <p className="font-medium">{collection.status}</p>
              </div>
            </div>
          </div>

          {/* Reason & Breakdown */}
          <div className="bg-background rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-text mb-4">
              Reason & Breakdown
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-neutral-dark">Reason</p>
                <p className="font-medium">{collection.reason}</p>
              </div>
              {collection.breakdown && (
                <div>
                  <p className="text-sm text-neutral-dark mb-2">
                    Amount Breakdown
                  </p>
                  <div className="bg-neutral-light rounded p-3 space-y-2">
                    {collection.breakdown.penalties > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Penalties</span>
                        <span className="font-medium">
                          {formatAmount(collection.breakdown.penalties)}
                        </span>
                      </div>
                    )}
                    {collection.breakdown.benefitReversal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Benefit Reversal</span>
                        <span className="font-medium">
                          {formatAmount(collection.breakdown.benefitReversal)}
                        </span>
                      </div>
                    )}
                    {collection.breakdown.otherCharges > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Other Charges</span>
                        <span className="font-medium">
                          {formatAmount(collection.breakdown.otherCharges)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment History */}
          {collection.collections && collection.collections.length > 0 && (
            <div className="bg-background rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-text mb-4">
                Payment History
              </h2>
              <div className="space-y-3">
                {collection.collections.map((payment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-success-light rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-text">
                        {formatAmount(payment.amount)}
                      </p>
                      <p className="text-sm text-neutral-dark">
                        {payment.method} • {formatDate(payment.collectedAt)}
                      </p>
                      {payment.reference && (
                        <p className="text-xs text-neutral-dark font-mono">
                          Ref: {payment.reference}
                        </p>
                      )}
                    </div>
                    <CheckCircleIcon className="h-6 w-6 text-success" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Write-off Details */}
          {collection.status === "WAIVED" && (
            <div className="bg-warning-light border border-warning rounded-lg p-6">
              <h2 className="text-lg font-semibold text-warning-dark mb-4">
                Write-off Information
              </h2>
              <div className="space-y-3">
                {collection.writeOffReason && (
                  <div>
                    <p className="text-sm text-warning-dark">Reason</p>
                    <p className="font-medium text-text">
                      {collection.writeOffReason}
                    </p>
                  </div>
                )}
                {collection.writtenOffAt && (
                  <div>
                    <p className="text-sm text-warning-dark">
                      Written Off Date
                    </p>
                    <p className="font-medium text-text">
                      {formatDate(collection.writtenOffAt)}
                    </p>
                  </div>
                )}
                {collection.writtenOffBy && (
                  <div>
                    <p className="text-sm text-warning-dark">Written Off By</p>
                    <p className="font-medium text-text">
                      {collection.writtenOffBy.name || "N/A"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Resolution Details */}
          {collection.resolvedAt && (
            <div className="bg-success-light border border-success rounded-lg p-6">
              <h2 className="text-lg font-semibold text-success-dark mb-4">
                Resolution Details
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-success-dark">Resolved Date</p>
                  <p className="font-medium text-text">
                    {formatDate(collection.resolvedAt)}
                  </p>
                </div>
                {collection.resolvedBy && (
                  <div>
                    <p className="text-sm text-success-dark">Resolved By</p>
                    <p className="font-medium text-text">
                      {collection.resolvedBy.name || "N/A"}
                    </p>
                  </div>
                )}
                {collection.resolutionNotes && (
                  <div>
                    <p className="text-sm text-success-dark">Notes</p>
                    <div className="bg-background rounded p-3 mt-1">
                      <p className="text-sm text-text whitespace-pre-wrap">
                        {collection.resolutionNotes}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {collection.resolutionNotes && collection.status !== "COLLECTED" && (
            <div className="bg-background rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-text mb-4">Notes</h2>
              <div className="bg-neutral-light rounded p-3">
                <p className="text-sm text-text whitespace-pre-wrap">
                  {collection.resolutionNotes}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <div className="bg-background rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-text mb-4">Actions</h2>
            <div className="space-y-3">
              {(collection.status === "PENDING" ||
                collection.status === "NOTIFIED" ||
                collection.status === "PARTIAL_COLLECTED") && (
                <>
                  <button
                    onClick={() => {
                      setPaymentData({
                        amount: remaining.toString(),
                        method: "CASH",
                        reference: "",
                      });
                      setShowPaymentModal(true);
                    }}
                    disabled={actionLoading}
                    className="w-full px-4 py-2 bg-success text-white rounded-lg hover:bg-success-dark disabled:opacity-50 flex items-center justify-center"
                  >
                    <CurrencyRupeeIcon className="h-5 w-5 mr-2" />
                    Record Payment
                  </button>
                  <button
                    onClick={() => setShowWaiveModal(true)}
                    disabled={actionLoading}
                    className="w-full px-4 py-2 bg-warning text-white rounded-lg hover:bg-warning-dark disabled:opacity-50"
                  >
                    Waive Collection
                  </button>
                </>
              )}
              {collection.status === "PARTIAL_COLLECTED" &&
                collection.collectedAmount >= collection.amount && (
                  <button
                    onClick={() => setShowResolveModal(true)}
                    disabled={actionLoading}
                    className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center justify-center"
                  >
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Mark as Resolved
                  </button>
                )}
              <button
                onClick={() => navigate(`/jewellery/${collection.planId?._id}`)}
                className="w-full px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary-light"
              >
                View Plan
              </button>
              <button
                onClick={() => window.print()}
                className="w-full px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light"
              >
                Print Details
              </button>
            </div>
          </div>

          {/* Status Info */}
          <div className="bg-background rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-text mb-4">
              Collection Status
            </h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                    <CalendarIcon className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-text">Created</p>
                  <p className="text-xs text-neutral-dark">
                    {formatDate(collection.createdAt)}
                  </p>
                </div>
              </div>

              {collection.collections && collection.collections.length > 0 && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-success flex items-center justify-center">
                      <CurrencyRupeeIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-text">
                      {collection.collections.length} Payment(s)
                    </p>
                    <p className="text-xs text-neutral-dark">
                      Last:{" "}
                      {formatDate(
                        collection.collections[
                          collection.collections.length - 1
                        ]?.collectedAt
                      )}
                    </p>
                  </div>
                </div>
              )}

              {collection.resolvedAt && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-success flex items-center justify-center">
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-text">Resolved</p>
                    <p className="text-xs text-neutral-dark">
                      {formatDate(collection.resolvedAt)}
                    </p>
                  </div>
                </div>
              )}

              {collection.status === "WAIVED" && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-warning flex items-center justify-center">
                      <XCircleIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-text">Waived</p>
                    <p className="text-xs text-neutral-dark">
                      {formatDate(collection.writtenOffAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-secondary-light rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-text mb-3">Quick Stats</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-dark">Total Payments</span>
                <span className="font-medium">
                  {collection.collections?.length || 0}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-dark">Days Outstanding</span>
                <span className="font-medium">
                  {Math.floor(
                    (new Date() - new Date(collection.createdAt)) /
                      (1000 * 60 * 60 * 24)
                  )}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-dark">Collection Rate</span>
                <span className="font-medium">{percentage.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Record Payment
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Plan Number</p>
              <p className="font-medium">
                {collection.planId?.planNumber || "N/A"}
              </p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Remaining Amount</p>
              <p className="text-lg font-bold text-text">
                {formatAmount(remaining)}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Payment Amount *
              </label>
              <input
                type="number"
                value={paymentData.amount}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, amount: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Enter amount..."
                min="0"
                step="0.01"
                max={remaining}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Payment Method *
              </label>
              <select
                value={paymentData.method}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, method: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
              >
                {paymentMethods.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Reference (Optional)
              </label>
              <input
                type="text"
                value={paymentData.reference}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, reference: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Transaction reference..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentData({ amount: "", method: "CASH", reference: "" });
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={actionLoading || !paymentData.amount}
                className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success-dark disabled:opacity-50"
              >
                {actionLoading ? "Recording..." : "Record Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waive Modal */}
      {showWaiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Waive Collection
            </h3>
            <div className="mb-4 p-3 bg-warning-light rounded-lg">
              <p className="text-sm text-warning-dark">
                ⚠️ This action will write off the outstanding amount. This
                cannot be undone.
              </p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Amount to Waive</p>
              <p className="text-lg font-bold text-danger">
                {formatAmount(remaining)}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Reason for Waiving *
              </label>
              <textarea
                value={waiveData.reason}
                onChange={(e) =>
                  setWaiveData({ ...waiveData, reason: e.target.value })
                }
                rows={3}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Explain why this amount is being waived..."
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Approval Note (Optional)
              </label>
              <textarea
                value={waiveData.approvalNote}
                onChange={(e) =>
                  setWaiveData({ ...waiveData, approvalNote: e.target.value })
                }
                rows={2}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Add approval notes..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowWaiveModal(false);
                  setWaiveData({ reason: "", approvalNote: "" });
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleWaive}
                disabled={actionLoading || !waiveData.reason}
                className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark disabled:opacity-50"
              >
                {actionLoading ? "Waiving..." : "Waive Collection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Resolve Collection
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">
                Mark this collection as fully resolved.
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Resolution Note (Optional)
              </label>
              <textarea
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Add any final notes..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowResolveModal(false);
                  setResolveNote("");
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResolve}
                disabled={actionLoading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              >
                {actionLoading ? "Resolving..." : "Mark as Resolved"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionDetails;
